import { useState, useEffect } from 'react';
import NextLink from 'next/link';
import {
  Field as PField,
  Notification,
  Button,
  Heading,
  Icon,
  yogoRedIcon,
  Link,
  LoadingIndicator,
} from '@lululemon/b2b-ecom-pattern-library';
import { Formik, Form, Field, FormikProps } from 'formik';
import { useFormat } from 'helpers/hooks/useFormat';
import useValidate from 'helpers/hooks/useValidate';
import { AUTH } from 'helpers/utils/googleAnalyticsEvents';
import { useAnalytics } from 'hooks/useAnalytics';
import { useAccount } from 'frontastic';
import styles from './forgot-password.module.scss';
import { useRouter } from 'next/router';

type Props = {
  errorMessages: {
    emailRequired: string;
    emailInvalid: string;
  };
  expired?: string | string[];
};

const ForgotPasswordForm = ({ errorMessages, expired: islinkExpired }: Props) => {
  //i18n messages
  const { formatMessage: formatErrorMessage } = useFormat({ name: 'error' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { validateEmail } = useValidate();
  //account actions
  const { requestPasswordReset, logout, loggedIn } = useAccount(); //Google Analytics
  const { trackEvent, EVENT_CATEGORY } = useAnalytics();
  const router = useRouter();
  const { forgotPasswordModal } = router.query;
  //error
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(forgotPasswordModal);
  //success
  const [success, setSuccess] = useState('');
  //processing...
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (forgotPasswordModal && loggedIn) {
      logout();
    }
    if (forgotPasswordModal && !loggedIn) {
      const handlePopState = () => {
        router.replace('/login');
      };

      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [forgotPasswordModal, loggedIn, router]);

  const resetFeedback = () => {
    setValidationErrors('');
    setSuccess('');
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validEmail = validateEmail(e.target.value);
    if (!e.target.value) {
      setValidationErrors(errorMessages?.emailRequired ?? 'Please enter an email address.');
      setIsValid(validEmail);
    } else if (!validEmail) {
      setValidationErrors(
        errorMessages?.emailInvalid ??
          'Email address is not in the correct format (xxx@yyy.zzz). Please correct the email address.',
      );
      setIsValid(validEmail);
    } else {
      setValidationErrors('');
      setIsValid(validEmail);
    }
  };
  const handleSubmit = async (values: typeof initialValues) => {
    trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, AUTH.RESET_PASSWORD_BUTTON_CLICKED);
    const { email } = values;
    setLoading(true);
    resetFeedback();
    try {
      const result = await requestPasswordReset(email);
      if (result?.isError) {
        const errorMessage = formatErrorMessage({
          id: 'forgot.password.wentWrong',
          defaultMessage: "If you have an account with us you'll receive instructions to reset your password by email",
        });
        setError(errorMessage);
        setLoading(false);
        trackEvent(EVENT_CATEGORY.ERROR, AUTH.FORGOT_PASSWORD_API_ERROR);
        return;
      } else {
        setIsEmailSent('true');
        trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, AUTH.FORGOT_PASSWORD_API_RESPONSE);
      }
      setLoading(false);
      trackEvent(EVENT_CATEGORY.APP_RESPONSE, AUTH.FORGOT_PASSWORD_API_RESPONSE);
      return;
    } catch (error) {
      setError(formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }));
      setLoading(false);
      trackEvent(EVENT_CATEGORY.ERROR, AUTH.FORGOT_PASSWORD_API_ERROR);
      return;
    }
  };

  const validateEmailInput = (email: string) => {
    const validEmail = validateEmail(email);
    if (!email) {
      return errorMessages?.emailRequired ?? 'Please enter an email address.';
    } else if (!validEmail) {
      return (
        errorMessages?.emailInvalid ??
        'Email address is not in the correct format (xxx@yyy.zzz). Please correct the email address.'
      );
    }
    return '';
  };

  const feedback = error || success;

  const initialValues = {
    email: '',
  };

  return (
    <>
      {loading && (
        <div className={styles.loadingIndicator}>
          <LoadingIndicator color={'red'} data-testid={'forgot-password-form__loading-indicator_test-id'} />
        </div>
      )}

      <div className="flex auto-cols-max flex-row gap-4">
        <div className="flex flex-col">
          <NextLink href="/login">
            <Icon content={yogoRedIcon} className="mb-15 ml-20 size-54 cursor-pointer" />
          </NextLink>

          <div className="px-24">
            <Heading
              tag="h1"
              className="lll-type-heading-extra-small py-10"
              data-testid="forgot-password-page__headline-text_test-id"
            >
              {!islinkExpired
                ? formatAccountMessage({
                    id: 'forgot.password.headline',
                    defaultMessage: 'Set a new password',
                  })
                : formatAccountMessage({
                    id: 'forgot.password.linkExpiry.headline',
                    defaultMessage: 'Your password link has expired',
                  })}
            </Heading>
            {isEmailSent ? (
              <>
                <span
                  className="lll-font-weight-regular lll-type-body-large flex w-4/5 flex-row flex-wrap py-4"
                  data-testid="forgot-password-page__description-email-sent_test-id"
                >
                  {formatAccountMessage({
                    id: 'forgot.password.emailSent',
                    defaultMessage: `Keep an eye out for your password link! Please note you'll only receive a link if the email address you entered is associated with a lululemon account.`,
                  })}
                </span>
                <div className={styles.backtolink}>
                  <Link
                    className="lll-type-body-large ml-0 mt-16 flex"
                    variant="underline"
                    data-testid="forgot-password-form__back-link_test-id"
                    tag="button"
                    onClick={() => router.push('/login')}
                  >
                    {formatAccountMessage({ id: 'account.back.sign', defaultMessage: 'Back to sign in' })}
                  </Link>
                </div>
              </>
            ) : (
              <>
                {!islinkExpired ? (
                  <span
                    className="lll-font-weight-regular lll-type-body-large flex w-4/5 flex-row flex-wrap pb-4 pt-6"
                    data-testid="forgot-password-page__description_test-id"
                  >
                    {formatAccountMessage({
                      id: 'forgot.password.textContainer',
                      defaultMessage: `Enter the email address associated with your lululemon account and we'll send you a link to reset your password.`,
                    })}
                  </span>
                ) : (
                  <span
                    className="lll-font-weight-regular lll-type-body-large flex w-4/5 flex-row flex-wrap pb-4 pt-6"
                    data-testid="forgot-password-page__Link-Expired-description_test-id"
                  >
                    {formatAccountMessage({
                      id: 'forgot.password.linkExpiryTextContainer',
                      defaultMessage: `Not to worry! Enter the email address associated with your lululemon account and we'll send you another.`,
                    })}
                  </span>
                )}
                <Formik
                  initialValues={initialValues}
                  onSubmit={handleSubmit}
                  validate={(values) => {
                    const errors: { email?: string } = {};
                    const emailError = validateEmailInput(values.email);
                    if (emailError) {
                      errors.email = emailError;
                      setIsValid(false);
                    } else {
                      setIsValid(true);
                    }
                    setValidationErrors(emailError);
                    return errors;
                  }}
                >
                  {({ values, handleChange }: FormikProps<typeof initialValues>) => (
                    <Form className="w-[70%] py-12" id="submit-login">
                      <Notification visible={!!feedback.length} type={!!error.length ? 'error' : 'success'}>
                        {feedback}
                      </Notification>
                      <Field
                        type="email"
                        id="required-email"
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        hint={!isValid && validationErrors ? validationErrors : null}
                        status={!isValid && validationErrors ? 'error' : ''}
                        data-testid={'forgot-password-page__email-input_test-id'}
                        as={PField}
                        className="lll-type-body-medium"
                      >
                        {formatAccountMessage({ id: 'forgot.password.email', defaultMessage: 'Email Address' })}
                      </Field>
                      <div className="flex justify-center">
                        <Button
                          type="submit"
                          className="mb-14 mt-16 w-full text-16 font-medium leading-tight"
                          kind="primary"
                          disabled={!isValid}
                          data-testid={'forgot-password-page__submit-button_test-id'}
                        >
                          {formatAccountMessage({ id: 'forgot.password.reset.link', defaultMessage: 'Send Email' })}
                        </Button>
                      </div>
                      <div className="flex justify-center">
                        <Link
                          tag="button"
                          className="lll-type-body-medium"
                          variant="underline"
                          data-testid="forgot-password-form__back-link_test-id"
                          onClick={() => router.push('/login')}
                        >
                          {formatAccountMessage({ id: 'account.back.sign', defaultMessage: 'Back to sign in' })}
                        </Link>
                      </div>
                    </Form>
                  )}
                </Formik>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordForm;
